import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui'

export interface IncompleteShiftConfirmModalProps {
  open: boolean
  issues: string[]
  confirmLoading?: boolean
  onConfirm: () => void
  onClose: () => void
}

export function IncompleteShiftConfirmModal({
  open,
  issues,
  confirmLoading = false,
  onConfirm,
  onClose,
}: IncompleteShiftConfirmModalProps): React.ReactElement | null {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="incomplete-shift-title"
      aria-describedby="incomplete-shift-description"
    >
      <button
        type="button"
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close incomplete shift warning"
      />
      <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
            <AlertTriangle className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2
              id="incomplete-shift-title"
              className="text-lg font-semibold text-gray-950 dark:text-gray-100"
            >
              This shift is incomplete
            </h2>
            <p
              id="incomplete-shift-description"
              className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300"
            >
              Some required shift details are missing or incomplete. Proceeding
              anyway can create inaccurate records, payroll issues, or policy
              violations.
            </p>
          </div>
        </div>

        {issues.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/70 dark:bg-amber-950/30">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-800 dark:text-amber-200">
              Missing information
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900 dark:text-amber-100">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={confirmLoading}
          >
            Cancel / Go Back
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            disabled={confirmLoading}
          >
            {confirmLoading ? '...' : 'Confirm / Proceed Anyway'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

IncompleteShiftConfirmModal.displayName = 'IncompleteShiftConfirmModal'
